import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { refreshQueries } from '../refresh';
import { api } from '../api';

export const NOTIFICATIONS_KEY = ['notifications'] as const;
export const FAILED_RESOURCE = 'notifications.failed';

/** One message of my inbox (GET /api/me/notifications). */
export type InboxItem = { id: string; subject: string; body: string; link: string | null; createdAt: string; readAt: string | null };
export type Inbox = { notifications: InboxItem[]; unread: number };

export type FailedItem = {
  id: string;
  channel: 'email' | 'sms' | 'push';
  toAddress: string | null;
  recipientName: string | null;
  subject: string;
  attempts: number;
  lastError: string | null;
  createdAt: string;
};

const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
const WHEN_YEAR = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** "19 października, 14:05" (with the year when it is not this year), Warsaw time. */
export function formatWhen(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const year = (x: Date) => new Intl.DateTimeFormat('en', { timeZone: 'Europe/Warsaw', year: 'numeric' }).format(x);
  return (year(d) === year(now) ? WHEN : WHEN_YEAR).format(d);
}

/** Opening a message: mark it read, then go where it points (no link: stay). */
export function useOpenNotification(onOpen?: () => void) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const read = useMutation({
    mutationFn: (n: InboxItem) => (n.readAt ? Promise.resolve() : api(`/api/me/notifications/${n.id}/read`, { method: 'POST' })),
    // the inbox page the message links to starts loading at once: its read must not pre-date the "read" write
    onSettled: () => refreshQueries(queryClient, NOTIFICATIONS_KEY),
  });
  return (n: InboxItem) => {
    read.mutate(n);
    onOpen?.();
    if (n.link) navigate(n.link);
  };
}
